import { useEffect, useState } from 'react';
import styled from 'styled-components';

import { useMutation } from '@tanstack/react-query';

import { Chip, Mono, Muted } from 'components/admin/clients/importConfigurations/mappings/Chips';
import { GroupOption, useGroupOptions } from 'components/admin/clients/importConfigurations/mappings/MapPanel';
import Button from 'components/shared/Button';
import ButtonGroup from 'components/shared/ButtonGroup';
import CloseButton from 'components/shared/CloseButton';
import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import Input from 'components/shared/form/Input';
import InputGroup from 'components/shared/form/InputGroup';
import Menu from 'components/shared/menu';
import { HOSPITAL } from 'constants/locationTypes';
import {
  adminApproveNewHospital,
  adminDeclineNewHospital,
  DeclineReason,
  Filing,
  FilingOption,
  FilingPick,
  NewHospital,
  newHospitalWriteError,
} from 'services/api/admin/newHospitals';
import { useToastActions } from 'stores/toastStore';
import { colors } from 'styles/theme/colors';

import {
  addressLine,
  confidence,
  DECLINE_LABEL,
  optionForFiling,
  optionLabel,
  shortDate,
  STATUS,
  writeErrorMessage,
} from './helpers';

type WriteError = { code: string | null; message: string; groupId: string | null };

type Props = {
  row: NewHospital;
  onClose: () => void;
  onDone: () => void;
  onReload: () => void;
};

function initialName(row: NewHospital) {
  if (row.filing?.kind === 'new_client') return row.filing.name;
  const option = row.filingOptions.find((o) => o.kind === 'new_client');
  return option ? optionLabel(option) : '';
}

export default function NewHospitalSlideout({ row, onClose, onDone, onReload }: Props) {
  const { addToast } = useToastActions();
  const done = row.status === 'created' || row.status === 'declined';
  const needsWhich = row.status === 'which_hospital';
  const [whichCcn, setWhichCcn] = useState<string | null>(needsWhich ? null : row.ccn);
  const [pickKey, setPickKey] = useState<string | null>(optionForFiling(row.filingOptions, row.filing)?.key ?? null);
  const [newName, setNewName] = useState(initialName(row));
  const [pickingGroup, setPickingGroup] = useState(false);
  const [group, setGroup] = useState<GroupOption | null>(null);
  const [error, setError] = useState<WriteError | null>(null);
  const groupOptions = useGroupOptions({ type: HOSPITAL });

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !e.defaultPrevented) onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const fail = (err: unknown) => {
    const { code, groupId } = newHospitalWriteError(err);
    const message = writeErrorMessage(err);
    setError({ code, message, groupId });
    addToast({ text: message });
    if (code === 'filing_changed') onReload();
  };

  const approve = useMutation({
    mutationFn: (filing: Filing) =>
      adminApproveNewHospital(row.ccn, { version: row.version, ccn: whichCcn ?? row.ccn, filing }),
    onSuccess: () => {
      addToast({ text: `Created ${chosen.name}` });
      onDone();
    },
    onError: fail,
  });

  const decline = useMutation({
    mutationFn: ({ reason, groupId }: { reason: DeclineReason; groupId?: string }) =>
      adminDeclineNewHospital(row.ccn, { version: row.version, reason, ...(groupId ? { groupId } : {}) }),
    onSuccess: (_data, { reason }) => {
      addToast({
        text:
          reason === 'existing_group'
            ? 'Mapped to the existing hospital.'
            : 'Declined. The agent won’t draft it again.',
      });
      onDone();
    },
    onError: fail,
  });

  const hospitals = [
    { ccn: row.ccn, name: row.name, address: row.address, distanceMiles: null as number | null },
    ...row.alternatives,
  ];
  const chosen = hospitals.find((h) => h.ccn === whichCcn) ?? hospitals[0];
  const alternativeChosen = chosen.ccn !== row.ccn;
  const option = row.filingOptions.find((o) => o.key === pickKey);
  const busy = approve.isPending || decline.isPending;
  const canApprove =
    !done && !busy && !!whichCcn && !!option && (option.kind === 'existing_client' || !!newName.trim());
  const receivingClients = [...new Map(row.received.map((r) => [r.clientId, r.clientName])).values()];

  const submitApprove = () => {
    if (!option) return;
    setError(null);
    approve.mutate(
      option.kind === 'existing_client'
        ? { kind: 'existing_client', clientId: option.clientId }
        : { kind: 'new_client', name: newName.trim() }
    );
  };

  const submitDecline = (reason: DeclineReason, groupId?: string) => {
    setError(null);
    decline.mutate({ reason, groupId });
  };

  return (
    <Panel>
      <PanelHeader>
        <HeaderText>
          <Chip $tone={STATUS[row.status].tone}>{STATUS[row.status].label}</Chip>
          <PanelTitle>{row.name}</PanelTitle>
          <Muted>Not in Olio · found in CMS</Muted>
        </HeaderText>
        <CloseButton onClick={onClose} />
      </PanelHeader>

      <PanelContent>
        {error && (
          <ErrorBox>
            {error.message}
            {error.code === 'draft_changed' && (
              <Button size='xsmall' variant='outline-danger' onClick={onReload}>
                Reload
              </Button>
            )}
            {error.code === 'hospital_exists' && error.groupId && (
              <Button
                size='xsmall'
                variant='outline-danger'
                disabled={busy}
                onClick={() => submitDecline('existing_group', error.groupId!)}>
                Map to that hospital
              </Button>
            )}
          </ErrorBox>
        )}

        {pickingGroup ? (
          <Section>
            <Label>Which Olio hospital is it?</Label>
            <Muted>
              Every received name below is mapped to the hospital you pick, and the agent doesn’t draft this one again.
            </Muted>
            <InputGroup title='Olio hospital'>
              <Select<GroupOption>
                {...groupOptions}
                value={group}
                onChange={setGroup}
                getOptionValue={(o) => o.value}
                getOptionLabel={(o) => o.label}
                placeholder='Search hospitals'
                menuPortalTarget={document.body}
              />
            </InputGroup>
            <ButtonGroup>
              <Button variant='ghost' onClick={() => setPickingGroup(false)}>
                Back
              </Button>
              <Button
                disabled={!group || busy}
                loading={decline.isPending}
                onClick={() => group && submitDecline('existing_group', group.value)}>
                Map to this hospital
              </Button>
            </ButtonGroup>
          </Section>
        ) : (
          <>
            {needsWhich && (
              <Section>
                <Label>Which hospital is it?</Label>
                {hospitals.map((h) => (
                  <Option key={h.ccn} $on={whichCcn === h.ccn}>
                    <input
                      type='radio'
                      name='which'
                      checked={whichCcn === h.ccn}
                      disabled={done}
                      onChange={() => setWhichCcn(h.ccn)}
                    />
                    <OptionText>
                      <OptionTitle>{h.name}</OptionTitle>
                      <Muted>
                        CCN {h.ccn} · {addressLine(h.address)}
                        {h.distanceMiles != null && ` · ${h.distanceMiles.toFixed(0)} mi from ${row.name}`}
                      </Muted>
                    </OptionText>
                  </Option>
                ))}
                <Muted>More than one open CMS hospital fits the name. Pick one before filing.</Muted>
              </Section>
            )}

            <Section>
              <Label>Hospital</Label>
              <Facts>
                <dt>Address</dt>
                <dd>{addressLine(chosen.address) || '—'}</dd>
                <dt>CCN</dt>
                <dd>
                  <Mono>{chosen.ccn}</Mono>
                </dd>
                {chosen.ccn === row.ccn && (
                  <>
                    <dt>Type</dt>
                    <dd>{row.providerType || '—'}</dd>
                    <dt>Health system</dt>
                    <dd>{row.healthSystem?.name ?? 'None listed'}</dd>
                    {row.identityConfidence != null && (
                      <>
                        <dt>Match confidence</dt>
                        <dd>{confidence(row.identityConfidence)}</dd>
                      </>
                    )}
                  </>
                )}
              </Facts>
              {row.closestOlioHospital && (
                <Closest>
                  <span>
                    Closest Olio hospital: {row.closestOlioHospital.name}
                    {row.closestOlioHospital.distanceMiles != null &&
                      ` · ${row.closestOlioHospital.distanceMiles.toFixed(0)} mi`}
                  </span>
                  {!done && (
                    <Button
                      size='xsmall'
                      variant='ghost'
                      disabled={busy}
                      onClick={() => submitDecline('existing_group', row.closestOlioHospital!.groupId)}>
                      It’s that one
                    </Button>
                  )}
                </Closest>
              )}
            </Section>

            <Section>
              <Label>Received as</Label>
              <Received>
                {row.received.map((r) => (
                  <ReceivedRow key={`${r.importConfigurationId}|${r.key}`}>
                    <span>
                      <Mono>{r.key}</Mono> · {r.clientName}
                    </span>
                    <Muted>
                      {[r.sourceSystem, r.firstSeenAt && `first ${shortDate(r.firstSeenAt)}`]
                        .concat(r.lastSeenAt ? [`last ${shortDate(r.lastSeenAt)}`] : [])
                        .filter(Boolean)
                        .join(' · ')}
                    </Muted>
                  </ReceivedRow>
                ))}
              </Received>
            </Section>

            {done ? (
              <DoneSummary row={row} />
            ) : (
              <>
                <Section>
                  <Label>File under</Label>
                  {row.filingOptions.map((o) => (
                    <Option key={o.key} $on={pickKey === o.key}>
                      <input
                        type='radio'
                        name='filing'
                        checked={pickKey === o.key}
                        onChange={() => setPickKey(o.key)}
                      />
                      <OptionText>
                        <OptionTitle>
                          {o.kind === 'new_client' ? <Chip $tone='blue'>New client</Chip> : o.clientName}
                        </OptionTitle>
                        <Muted>{optionDetail(o)}</Muted>
                        {o.kind === 'new_client' && (
                          <Input
                            value={newName}
                            aria-label='New client name'
                            onChange={(e) => setNewName(e.target.value)}
                            onFocus={() => setPickKey(o.key)}
                          />
                        )}
                      </OptionText>
                    </Option>
                  ))}
                  {row.filingOptions.length === 0 && <Muted>No filing options. Reload to recompute them.</Muted>}
                  {alternativeChosen && (
                    <Muted>
                      These options were worked out for {row.name}. The client, location and new-client name for{' '}
                      {chosen.name} are worked out on approve.
                    </Muted>
                  )}
                  <Muted>Only clients with no users are offered.</Muted>
                </Section>

                <Section>
                  <Label>Where the picks came from</Label>
                  <Picks>
                    <PickBox who='Rules' pick={row.rules} options={row.filingOptions} />
                    {row.jev && <PickBox who='Jev' pick={row.jev} options={row.filingOptions} />}
                  </Picks>
                  <Muted>{pickNote(row.rules, row.jev)}</Muted>
                </Section>

                <Section>
                  <Label>Approving creates</Label>
                  <Creates>
                    <li>
                      <CreateLabel>Client</CreateLabel>
                      <span>
                        {alternativeChosen ? (
                          <Muted>Worked out on approve for {chosen.name}</Muted>
                        ) : option ? (
                          <>
                            <NewPill isNew={option.kind === 'new_client'} />{' '}
                            {option.kind === 'new_client' ? newName.trim() || '…' : option.clientName}
                          </>
                        ) : (
                          <Muted>Pick where to file it</Muted>
                        )}
                      </span>
                    </li>
                    <li>
                      <CreateLabel>Location</CreateLabel>
                      <span>
                        {alternativeChosen ? (
                          <Muted>Worked out on approve · </Muted>
                        ) : (
                          <NewPill isNew={!option?.existingLocationId} />
                        )}{' '}
                        {chosen.name}
                        <Sub>{addressLine(chosen.address)}</Sub>
                      </span>
                    </li>
                    <li>
                      <CreateLabel>Hospital</CreateLabel>
                      <span>
                        <NewPill isNew /> {chosen.name}
                        <Sub>CCN {chosen.ccn}</Sub>
                      </span>
                    </li>
                    <li>
                      <CreateLabel>Network</CreateLabel>
                      <span>Adds it for {receivingClients.join(', ')}</span>
                    </li>
                    <li>
                      <CreateLabel>Mappings</CreateLabel>
                      <span>
                        {row.received.map((r, i) => (
                          <span key={`${r.importConfigurationId}|${r.key}`}>
                            {i > 0 && ', '}
                            <Mono>{r.key}</Mono>
                          </span>
                        ))}{' '}
                        → this hospital
                      </span>
                    </li>
                  </Creates>
                </Section>
              </>
            )}
          </>
        )}
      </PanelContent>

      <PanelFooter>
        {done || pickingGroup ? (
          <>
            <span />
            <Button variant='ghost' onClick={onClose}>
              Close
            </Button>
          </>
        ) : (
          <>
            <Menu>
              <Menu.Trigger disabled={busy}>
                <Button variant='outline-danger' disabled={busy}>
                  Don’t create ▾
                </Button>
              </Menu.Trigger>
              <Menu.Content position='left'>
                <Menu.Item onClick={() => setPickingGroup(true)}>It’s an existing Olio hospital…</Menu.Item>
                <Menu.Item onClick={() => submitDecline('not_hospital')}>Not a hospital</Menu.Item>
                <Menu.Item onClick={() => submitDecline('dont_create')}>Don’t create it</Menu.Item>
              </Menu.Content>
            </Menu>
            <ButtonGroup>
              <Button variant='ghost' onClick={onClose}>
                Later
              </Button>
              <Button disabled={!canApprove} loading={approve.isPending} onClick={submitApprove}>
                Approve and create
              </Button>
            </ButtonGroup>
          </>
        )}
      </PanelFooter>
    </Panel>
  );
}

function optionDetail(option: FilingOption) {
  if (option.kind === 'new_client') return 'A new client, not contracted, with no users.';
  const parts = [
    `${option.hospitalCount} hospital${option.hospitalCount === 1 ? '' : 's'}`,
    option.states.length ? `in ${option.states.join(', ')}` : '',
  ].filter(Boolean);
  return `No users · ${parts.join(' ')}${option.existingLocationId ? ' · already has a location at this address' : ''}`;
}

function sameFiling(a: FilingPick['filing'], b: FilingPick['filing']) {
  if (!a || !b || a.kind !== b.kind) return false;
  return a.kind === 'new_client' || (b.kind === 'existing_client' && a.clientId === b.clientId);
}

function pickNote(rules: FilingPick | null, jev: FilingPick | null) {
  if (!rules) return 'No rules pick, so a person picks.';
  if (!jev) return 'Only the rules picked. There was one place to file it.';
  if (!sameFiling(rules.filing, jev.filing)) return 'They disagree, so a person picks.';
  return Math.min(rules.confidence, jev.confidence) >= 0.8
    ? 'They agree, both at 0.8 or above.'
    : 'They agree, but not both at 0.8 or above.';
}

function PickBox({ who, pick, options }: { who: string; pick: FilingPick | null; options: FilingOption[] }) {
  const option = pick ? optionForFiling(options, pick.filing ?? null) : undefined;
  return (
    <PickCard>
      <Muted>{who}</Muted>
      <span>
        {pick
          ? option
            ? `${optionLabel(option)}${option.kind === 'new_client' ? ' (new)' : ''}`
            : 'An option no longer offered'
          : 'No pick'}
      </span>
      {pick && <Mono>{confidence(pick.confidence)}</Mono>}
    </PickCard>
  );
}

function NewPill({ isNew }: { isNew: boolean }) {
  return <Chip $tone={isNew ? 'blue' : 'neutral'}>{isNew ? 'New' : 'Existing'}</Chip>;
}

function DoneSummary({ row }: { row: NewHospital }) {
  const by = row.decidedBy?.name ?? (row.status === 'created' ? 'the agent' : null);
  const when = shortDate(row.decidedAt);
  const created = row.createdHospital;
  return (
    <Section>
      <Label>{row.status === 'created' ? 'Created' : 'Declined'}</Label>
      {created && (
        <span>
          {created.name} · CCN <Mono>{created.ccn}</Mono>
          <Sub>{addressLine(created.address)}</Sub>
        </span>
      )}
      <span>
        {row.status === 'declined' && row.declinedReason ? `${DECLINE_LABEL[row.declinedReason]}. ` : ''}
        {[by && `By ${by}`, when && `on ${when}`].filter(Boolean).join(' ')}
      </span>
    </Section>
  );
}

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: white;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 20px 24px;
  border-bottom: 1px solid ${colors.black10};
`;

const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
`;

const PanelTitle = styled.h2`
  font-size: 18px;
  font-weight: 500;
  margin: 0;
`;

const PanelContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const PanelFooter = styled.div`
  padding: 16px 24px;
  border-top: 1px solid ${colors.black10};
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-size: 14px;
`;

const Label = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: ${colors.black75};
`;

const Facts = styled.dl`
  display: grid;
  grid-template-columns: max-content minmax(0, 1fr);
  gap: 4px 16px;
  margin: 0;

  dt {
    color: ${colors.black50};
  }

  dd {
    margin: 0;
  }
`;

const Closest = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  padding: 8px 12px;
  border: 1px dashed ${colors.black25};
  border-radius: 4px;
`;

const Received = styled.div`
  border: 1px solid ${colors.black10};
  border-radius: 4px;
`;

const ReceivedRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px 12px;

  & + & {
    border-top: 1px solid ${colors.black10};
  }
`;

const Option = styled.label<{ $on: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid ${({ $on }) => ($on ? colors.blue100 : colors.black10)};
  background: ${({ $on }) => ($on ? colors.blue10 : 'white')};
  border-radius: 4px;
  cursor: pointer;

  input[type='radio'] {
    margin-top: 3px;
  }
`;

const OptionText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 0;
`;

const OptionTitle = styled.div`
  font-weight: 500;
`;

const Picks = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
`;

const PickCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px 12px;
  border: 1px solid ${colors.black10};
  border-radius: 4px;
`;

const Creates = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;

  li {
    display: grid;
    grid-template-columns: 80px minmax(0, 1fr);
    gap: 8px;
  }
`;

const CreateLabel = styled.span`
  color: ${colors.black50};
`;

const Sub = styled.div`
  font-size: 12px;
  color: ${colors.black50};
`;

const ErrorBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 4px;
  background: ${colors.red10};
  color: ${colors.red100};
  font-size: 14px;
`;
